import { Fragment, type ReactNode } from "react";
import { Link } from "react-router-dom";

/**
 * Minimal Markdown-ish renderer for long-form article bodies.
 * Supports:
 *  - `## H2`, `### H3` headings (rendered visually)
 *  - `#### H4` blocks — grouped into a click-to-expand FAQ accordion
 *  - Unordered lists (`- item`) and ordered lists (`1. item`)
 *  - Blockquotes (`> text`)
 *  - Pipe-tables (`| col | col |`) with a header separator row
 *  - Inline **bold**, *italic*, `code`, and [text](href) links
 *  - Blank-line-separated paragraphs
 *
 * Internal links (starting with `/`) are routed via react-router; external
 * links open in a new tab with rel="noopener".
 */
interface RichContentProps {
  body: string;
  /** Optional element injected into the article flow after the 3rd H2. */
  midCta?: ReactNode;
  /** Disable auto-linking of IRS forms/notices (default: enabled). */
  noAutoLink?: boolean;
}

// Auto-link map: bare IRS form / notice mentions get linked to their
// detail page on first occurrence per article. Improves internal linking
// and topical authority for AEO/LLM crawlers.
const NOTICE_LINKS: Array<[RegExp, string]> = [
  [/\bCP\s?14\b/g, "/resources/irs-notices/cp14"],
  [/\bCP\s?2000\b/g, "/resources/irs-notices/cp2000"],
  [/\bCP\s?501\b/g, "/resources/irs-notices/cp501"],
  [/\bCP\s?503\b/g, "/resources/irs-notices/cp503"],
  [/\bCP\s?504\b/g, "/resources/irs-notices/cp504"],
  [/\bCP\s?508C\b/gi, "/resources/irs-notices/cp508c"],
  [/\bCP\s?523\b/g, "/resources/irs-notices/cp523"],
  [/\b(?:LT\s?11|Letter\s?1058)\b/g, "/resources/irs-notices/lt11"],
  [/\bLetter\s?525\b/g, "/resources/irs-notices/letter525"],
  [/\bNotice of Deficiency\b/g, "/resources/irs-notices/notice-deficiency"],
];
const FORM_LINKS: Array<[RegExp, string]> = [
  [/\bForm\s656\b/g, "/resources/irs-forms#form-656"],
  [/\bForm\s433-?A\b/g, "/resources/irs-forms#form-433a"],
  [/\bForm\s433-?B\b/g, "/resources/irs-forms#form-433b"],
  [/\bForm\s2848\b/g, "/resources/irs-forms#form-2848"],
  [/\bForm\s8821\b/g, "/resources/irs-forms#form-8821"],
  [/\bForm\s9465\b/g, "/resources/irs-forms#form-9465"],
  [/\bForm\s12153\b/g, "/resources/irs-forms#form-12153"],
  [/\bForm\s8857\b/g, "/resources/irs-forms#form-8857"],
  [/\bForm\s843\b/g, "/resources/irs-forms#form-843"],
  [/\bForm\s4180\b/g, "/resources/irs-forms#form-4180"],
];
const AUTO_LINKS = [...NOTICE_LINKS, ...FORM_LINKS];

/** Wrap the first occurrence of each known IRS form/notice term in `text`
 *  with a Markdown link, unless `seen` already records that pattern. */
const applyAutoLinks = (text: string, seen: Set<string>): string => {
  let out = text;
  for (const [re, href] of AUTO_LINKS) {
    const key = re.source;
    if (seen.has(key)) continue;
    re.lastIndex = 0;
    const m = re.exec(out);
    if (!m) continue;
    // Skip if already inside a markdown link [..](..)
    const before = out.slice(0, m.index);
    const openBrackets = (before.match(/\[/g) || []).length;
    const closeBrackets = (before.match(/\]/g) || []).length;
    if (openBrackets > closeBrackets) continue;
    out = out.slice(0, m.index) + `[${m[0]}](${href})` + out.slice(m.index + m[0].length);
    seen.add(key);
  }
  return out;
};

const inline = (text: string, key = "i"): ReactNode[] => {
  const nodes: ReactNode[] = [];
  const regex =
    /(\[([^\]]+)\]\(([^)]+)\))|(\*\*([^*]+)\*\*)|(\*([^*]+)\*)|(`([^`]+)`)/g;
  let lastIndex = 0;
  let m: RegExpExecArray | null;
  let i = 0;
  while ((m = regex.exec(text)) !== null) {
    if (m.index > lastIndex) nodes.push(text.slice(lastIndex, m.index));
    const k = `${key}-${i++}`;
    if (m[1]) {
      const label = m[2];
      const href = m[3];
      nodes.push(
        href.startsWith("/") ? (
          <Link key={k} to={href} className="text-navy underline underline-offset-2 decoration-gold/60 hover:text-gold">
            {label}
          </Link>
        ) : (
          <a
            key={k}
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            className="text-navy underline underline-offset-2 decoration-gold/60 hover:text-gold"
          >
            {label}
          </a>
        ),
      );
    } else if (m[4]) {
      nodes.push(
        <strong key={k} className="font-bold text-navy">
          {m[5]}
        </strong>,
      );
    } else if (m[6]) {
      nodes.push(
        <em key={k} className="italic">
          {m[7]}
        </em>,
      );
    } else if (m[8]) {
      nodes.push(
        <code key={k} className="font-mono text-[0.92em] bg-secondary px-1.5 py-0.5 rounded">
          {m[9]}
        </code>,
      );
    }
    lastIndex = m.index + m[0].length;
  }
  if (lastIndex < text.length) nodes.push(text.slice(lastIndex));
  return nodes;
};

const renderTable = (lines: string[], key: string) => {
  const split = (row: string) =>
    row
      .replace(/^\||\|$/g, "")
      .split("|")
      .map((c) => c.trim());
  const header = split(lines[0]);
  const body = lines.slice(2).map(split);
  return (
    <div key={key} className="not-prose my-8 overflow-x-auto">
      <table className="w-full text-sm border border-border">
        <thead className="bg-secondary">
          <tr>
            {header.map((h, i) => (
              <th
                key={i}
                className="text-left font-heading font-bold text-navy px-4 py-3 border-b border-border"
              >
                {inline(h, `th-${i}`)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {body.map((row, r) => (
            <tr key={r} className="odd:bg-white even:bg-secondary/30">
              {row.map((cell, c) => (
                <td
                  key={c}
                  className="align-top px-4 py-3 border-b border-border text-foreground/85"
                >
                  {inline(cell, `td-${r}-${c}`)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const renderBlock = (block: string[], key: string): ReactNode => {
  let first = block[0].trim();

  // Horizontal rule
  if (/^(?:-{3,}|\*{3,}|(?:\*\s){2,}\*)$/.test(first)) {
    return <div key={key} className="h-4" aria-hidden="true" />;
  }

  // Standalone image
  const imgMatch = block.length === 1 && first.match(/^!\[([^\]]*)\]\(([^)]+)\)$/);
  if (imgMatch) {
    const [, alt, src] = imgMatch;
    if (src.startsWith("<") || src.toLowerCase().includes("base64-image-removed")) {
      return null;
    }
    return (
      <figure key={key} className="my-8">
        <img src={src} alt={alt} loading="lazy" className="w-full rounded-md bg-secondary"  width={1200} height={675} />
        {alt && (
          <figcaption className="mt-2 text-xs text-muted-foreground italic text-center">
            {alt}
          </figcaption>
        )}
      </figure>
    );
  }

  if (first.startsWith("\\>")) first = ">" + first.slice(2);

  if (first.startsWith("## ")) {
    const text = first.slice(3).replace(/^\*\*(.+)\*\*$/, "$1");
    return (
      <h2
        key={key}
        className="font-heading text-2xl md:text-3xl font-bold text-navy mt-10 mb-3 leading-[1.2]"
      >
        {inline(text, key)}
      </h2>
    );
  }
  if (first.startsWith("### ")) {
    const text = first.slice(4).replace(/^\*\*(.+)\*\*$/, "$1");
    return (
      <h3
        key={key}
        className="font-heading text-xl md:text-2xl font-bold text-navy mt-8 mb-2 leading-[1.25]"
      >
        {inline(text, key)}
      </h3>
    );
  }
  if (first.startsWith("> ")) {
    return (
      <blockquote
        key={key}
        className="border-l-4 border-gold bg-gold/5 pl-5 pr-4 py-3 italic text-foreground/85 my-6"
      >
        {block.map((l, i) => (
          <Fragment key={i}>
            {inline(l.replace(/^\\?>\s?/, ""), `${key}-${i}`)}{" "}
          </Fragment>
        ))}
      </blockquote>
    );
  }
  if (first.startsWith("|") && block[1]?.trim().startsWith("|")) {
    return renderTable(block, key);
  }
  if (/^[-*]\s+/.test(first)) {
    return (
      <ul key={key} className="space-y-2 list-disc pl-5 marker:text-gold">
        {block.map((l, i) => (
          <li key={i} className="text-foreground/85 leading-[1.7] pl-1">
            {inline(l.replace(/^[-*]\s+/, ""), `${key}-${i}`)}
          </li>
        ))}
      </ul>
    );
  }
  if (/^\d+\.\s+/.test(first)) {
    return (
      <ol key={key} className="space-y-2 list-decimal pl-5 marker:text-gold marker:font-bold">
        {block.map((l, i) => (
          <li key={i} className="text-foreground/85 leading-[1.7] pl-1">
            {inline(l.replace(/^\d+\.\s+/, ""), `${key}-${i}`)}
          </li>
        ))}
      </ol>
    );
  }
  // Default: paragraph
  const text = block.join(" ");
  return (
    <p key={key} className="text-foreground/85 leading-[1.75] text-[17px]">
      {inline(text, key)}
    </p>
  );
};

export const RichContent = ({ body, midCta, noAutoLink }: RichContentProps) => {
  // Strip residual WordPress comment-form scaffolding that came in via the
  // legacy site crawl ("### Leave a Reply", "Save my name…", etc.).
  let cleaned = body.replace(/\r\n/g, "\n");
  cleaned = cleaned.replace(/\n#{1,6}\s*Leave a Reply[\s\S]*$/i, "\n");
  cleaned = cleaned.replace(
    /^\s*Your email address will not be published[\s\S]*?next time I comment\.?\s*$/gim,
    "",
  );

  // Auto-link the first mention of each IRS form / notice term to its
  // resource page — boosts internal linking + topical authority. Skip
  // headings / blockquotes / tables so anchor text stays in body prose.
  if (!noAutoLink) {
    const seen = new Set<string>();
    cleaned = cleaned
      .split("\n")
      .map((line) => {
        const t = line.trim();
        if (!t || /^[#>|]/.test(t) || /^!\[/.test(t)) return line;
        return applyAutoLinks(line, seen);
      })
      .join("\n");
  }
  cleaned = cleaned.replace(/^\s*(Comment|Name|Email|Website)\s*\\?\*?\s*$/gim, "");

  // Split into blocks separated by blank lines.
  const lines = cleaned.split("\n");
  const blocks: string[][] = [];
  let current: string[] = [];
  for (const line of lines) {
    if (line.trim() === "") {
      if (current.length) {
        blocks.push(current);
        current = [];
      }
    } else {
      current.push(line);
    }
  }
  if (current.length) blocks.push(current);

  // Group consecutive `#### Question` blocks + their following body into a
  // single click-to-expand FAQ accordion. WordPress posts use #### for
  // Q&A items, which previously rendered as orphaned paragraphs.
  type Group =
    | { kind: "block"; block: string[] }
    | { kind: "faq"; items: { q: string; a: string[][] }[] };
  const groups: Group[] = [];
  for (let i = 0; i < blocks.length; i++) {
    const b = blocks[i];
    const firstLine = b[0].trim();
    if (b.length === 1 && firstLine.startsWith("#### ")) {
      const question = firstLine.slice(5).replace(/^\*\*(.+)\*\*$/, "$1").replace(/\?+$/, "?");
      const answer: string[][] = [];
      let j = i + 1;
      while (j < blocks.length) {
        const next = blocks[j][0].trim();
        if (/^#{1,4}\s/.test(next)) break;
        answer.push(blocks[j]);
        j++;
      }
      i = j - 1;
      const last = groups[groups.length - 1];
      if (last && last.kind === "faq") {
        last.items.push({ q: question, a: answer });
      } else {
        groups.push({ kind: "faq", items: [{ q: question, a: answer }] });
      }
    } else {
      groups.push({ kind: "block", block: b });
    }
  }

  // Find which "block" group is the 3rd top-level H2 (for midCta injection).
  let h2Count = 0;
  let midCtaAfterIdx = -1;
  if (midCta) {
    for (let i = 0; i < groups.length; i++) {
      const g = groups[i];
      if (g.kind === "block" && g.block[0]?.trim().startsWith("## ")) {
        h2Count++;
        if (h2Count === 3) {
          midCtaAfterIdx = i;
          break;
        }
      }
    }
    // Fallback: inject at the middle if there aren't enough H2s.
    if (midCtaAfterIdx === -1) midCtaAfterIdx = Math.floor(groups.length / 2);
  }

  return (
    <div className="space-y-6">
      {groups.map((group, gIdx) => {
        const node =
          group.kind === "faq" ? (
            <div
              key={`faq-${gIdx}`}
              className="not-prose my-6 divide-y divide-border rounded-lg border border-border bg-white overflow-hidden"
            >
              {group.items.map((item, i) => (
                <details key={i} className="group open:bg-secondary/30">
                  <summary className="faq-question cursor-pointer list-none p-5 flex items-start justify-between gap-4 font-heading font-bold text-navy text-base md:text-lg hover:bg-secondary/40">
                    <span>{inline(item.q, `faq-${gIdx}-${i}`)}</span>
                    <span
                      aria-hidden="true"
                      className="mt-1 inline-block text-gold transition-transform group-open:rotate-45 text-2xl leading-none font-normal select-none"
                    >
                      +
                    </span>
                  </summary>
                  <div className="faq-answer px-5 pb-5 space-y-3">
                    {item.a.map((ab, j) => renderBlock(ab, `faq-${gIdx}-${i}-a-${j}`))}
                  </div>
                </details>
              ))}
            </div>
          ) : (
            <Fragment key={`g-${gIdx}`}>{renderBlock(group.block, `g-${gIdx}`)}</Fragment>
          );
        return (
          <Fragment key={`g-${gIdx}`}>
            {node}
            {gIdx === midCtaAfterIdx && midCta ? (
              <div className="not-prose my-8">{midCta}</div>
            ) : null}
          </Fragment>
        );
      })}
    </div>
  );
};
